import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  BuildingOfficeIcon,
  PlusIcon,
  TrashIcon,
  PencilIcon,
  MagnifyingGlassIcon
} from '@heroicons/react/24/outline';
import { ServiceFactory } from '../../services/factory/ServiceFactory';
import {
  Organization,
  OrganizationType,
  ORGANIZATION_TYPE_LABELS,
  ORGANIZATION_TYPE_COLORS,
  getOrganizationTypeLabel,
  isKyndlyOrg
} from '../../types/organization';
import OrganizationDialog from './OrganizationDialog';

const OrganizationManagement: React.FC = () => {
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState<OrganizationType | 'all'>('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingOrg, setEditingOrg] = useState<Organization | null>(null);

  const apiService = ServiceFactory.createApiService();

  // Fetch organizations
  const fetchOrganizations = async () => {
    try {
      setLoading(true);
      const data = await apiService.getOrganizations();
      setOrganizations(data);
      setError(null);
    } catch (err: any) {
      console.error('Error fetching organizations:', err);
      setError(err.message || 'Failed to load organizations');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrganizations();
  }, []);

  // Handle create
  const handleCreate = () => {
    setEditingOrg(null);
    setDialogOpen(true);
  };

  // Handle edit
  const handleEdit = (org: Organization) => {
    if (isKyndlyOrg(org)) {
      setError('Cannot edit Kyndly organization');
      return;
    }
    setEditingOrg(org);
    setDialogOpen(true);
  };

  // Handle delete
  const handleDelete = async (org: Organization) => {
    if (isKyndlyOrg(org)) {
      setError('Cannot delete Kyndly organization');
      return;
    }

    if (!window.confirm(`Are you sure you want to delete "${org.name}"?`)) {
      return;
    }

    try {
      await apiService.deleteOrganization(org.id);
      await fetchOrganizations();
      setError(null);
    } catch (err: any) {
      console.error('Error deleting organization:', err);
      setError(err.message || 'Failed to delete organization');
    }
  };

  // Handle save from dialog
  const handleSave = async () => {
    await fetchOrganizations();
    setDialogOpen(false);
    setEditingOrg(null);
  };

  // Filter and search organizations
  const filteredOrganizations = organizations.filter(org => {
    const matchesSearch = !searchTerm ||
      org.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      getOrganizationTypeLabel(org.organization_type).toLowerCase().includes(searchTerm.toLowerCase());

    const matchesFilter = filterType === 'all' || org.organization_type === filterType;

    return matchesSearch && matchesFilter;
  });

  // Sort: Admin type (Kyndly) first, then alphabetically
  const sortedOrganizations = [...filteredOrganizations].sort((a, b) => {
    if (a.organization_type === 'admin') return -1;
    if (b.organization_type === 'admin') return 1;
    return a.name.localeCompare(b.name);
  });

  return (
    <div className="bg-white dark:bg-night-800 rounded-brand shadow-brand dark:shadow-dark overflow-hidden">
      <div className="p-4">
        {/* Header */}
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-night dark:text-white">Organizations</h2>
          <button
            onClick={handleCreate}
            className="flex items-center text-sm px-4 py-2 bg-primary-500 text-white rounded-md hover:bg-primary-600 transition-colors"
          >
            <PlusIcon className="h-5 w-5 mr-1" />
            New Organization
          </button>
        </div>

        {/* Error display */}
        {error && (
          <div className="mb-4 bg-red-100 dark:bg-red-900/30 border border-red-400 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-3 rounded-md">
            <p>{error}</p>
          </div>
        )}

        {/* Search and Filter */}
        <div className="mb-4 flex gap-4">
          <div className="flex-1 relative">
            <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name or type..."
              className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-night-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-night-700 text-night dark:text-white"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value as OrganizationType | 'all')}
            className="px-3 py-2 border border-gray-300 dark:border-night-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-night-700 text-night dark:text-white"
          >
            <option value="all">All Types</option>
            <option value="tpa">TPA</option>
            <option value="broker">Broker</option>
            <option value="employer">Employer</option>
          </select>
        </div>

        {/* Table */}
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin h-10 w-10 border-4 border-primary-500 rounded-full border-t-transparent"></div>
            <span className="ml-3 text-night dark:text-white">Loading organizations...</span>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full divide-y divide-gray-200 dark:divide-night-700">
              <thead className="bg-gray-50 dark:bg-night-700">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Organization
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Type
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Created
                  </th>
                  <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-night-800 divide-y divide-gray-200 dark:divide-night-700">
                {sortedOrganizations.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-6 py-12 text-center text-gray-500 dark:text-gray-400">
                      {searchTerm || filterType !== 'all' ? 'No organizations match your search' : 'No organizations found'}
                    </td>
                  </tr>
                ) : (
                  sortedOrganizations.map((org) => {
                    const isProtected = isKyndlyOrg(org);
                    const colors = ORGANIZATION_TYPE_COLORS[org.organization_type];
                    const typeLabel = getOrganizationTypeLabel(org.organization_type);

                    return (
                      <motion.tr
                        key={org.id}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="hover:bg-gray-50 dark:hover:bg-night-700"
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-10 w-10 bg-primary-100 dark:bg-primary-900/30 rounded-full flex items-center justify-center">
                              <BuildingOfficeIcon className="h-5 w-5 text-primary-600 dark:text-primary-400" />
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-night dark:text-white">
                                {org.name}
                                {isProtected && (
                                  <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">(System)</span>
                                )}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${colors.bg} ${colors.text}`}>
                            {typeLabel}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                          {org.created_at ? new Date(org.created_at).toLocaleDateString() : '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex justify-end space-x-2">
                            {!isProtected && (
                              <>
                                <button
                                  onClick={() => handleEdit(org)}
                                  className="text-primary-600 hover:text-primary-900 dark:text-primary-400 dark:hover:text-primary-300 flex items-center"
                                >
                                  <PencilIcon className="h-4 w-4 mr-1" />
                                  Edit
                                </button>
                                <button
                                  onClick={() => handleDelete(org)}
                                  className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300 flex items-center"
                                >
                                  <TrashIcon className="h-4 w-4 mr-1" />
                                  Delete
                                </button>
                              </>
                            )}
                            {isProtected && (
                              <span className="text-gray-400 dark:text-gray-600 text-xs">Protected</span>
                            )}
                          </div>
                        </td>
                      </motion.tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Stats */}
        <div className="mt-4 flex justify-between items-center text-sm text-gray-500 dark:text-gray-400">
          <div>
            Showing {sortedOrganizations.length} of {organizations.length} organization{organizations.length !== 1 ? 's' : ''}
          </div>
        </div>
      </div>

      {/* Dialog */}
      {dialogOpen && (
        <OrganizationDialog
          organization={editingOrg}
          onClose={() => {
            setDialogOpen(false);
            setEditingOrg(null);
          }}
          onSave={handleSave}
        />
      )}
    </div>
  );
};

export default OrganizationManagement;
